import { SectionWithContent } from '@core';
import { Box, Grid, SmallText } from '@themed';
import { CountryDropdown } from '@types';
import React, { useRef, useState } from 'react';
import { ClosableColorPicker, useColorAtIndex } from '../../../colorpicker';
import { ComparisonHeader } from '../../../finnoscore-comparison/ComparisonSelector';
import { InstitutionDropDownWithState } from '../../../finnoscore-comparison/InstitutionGroupedDropDown';
import { CountrySelector } from '../../../finnoscore-list/CountrySelector';
import { LegacySwitch } from '../../comparison/components/LegacySwitch';
import { ChartItem, ExportButton, fillColorStyles, Monochrome } from '../../shared/components/base';
import { ChartDropDownHeader } from '../../shared/components/base/ChartDropDownControl';
import { ChartWizardStep } from '../../shared/components/base/ChartWizardStep';
import { finnoChartStyles } from '../../shared/components/base/index.styles';
import { ChartDropDownControlComponent } from '../../shared/components/control/ChartDropDownControl';
import { DefaultChartData } from '../../shared/helpers';
import { useCountryInstitutionSelection } from '../../shared/hooks/useCountryInstitutionSelection';
import { useInstitutionDropDownControl } from '../../shared/hooks/useInstitutionDropDownControl';
import { usePeriodDropDownControl } from '../../shared/hooks/usePeriodDropDownControl';
import { useTypeDropDownControl } from '../../shared/hooks/useTypeDropDownControl';
import { chartStyles } from '../../shared/styles';
import { InternationalComparisonChart } from './InternationalChart';

const contentStyles = {
  ...finnoChartStyles.content,
  gridTemplateRows: ['max-content max-content max-content auto auto auto'],
  gridTemplateColumns: '0.9fr 2fr',
};

interface InternationalChartProps {
  countries: CountryDropdown[];
}

export const InternationalChart = ({ countries }: InternationalChartProps) => {
  const exportChartRef = useRef(null);
  const [isMixed, setIsMixed] = useState<number>(0);

  const [colorA, setColorA] = useColorAtIndex(0);
  const [colorB, setColorB] = useColorAtIndex(1);

  const { types, setTypeId, typeId, type: fsType, isLoading: isTypeLoading } = useTypeDropDownControl();

  const {
    periods,
    period,
    periodId,
    setPeriodId,
    isLoading: isPeriodLoading,
  } = usePeriodDropDownControl({ type: fsType, paramKey: 'currentPeriodId' });

  const {
    institutions,
    institution,
    institutionSetHandler,
    isLoading: isInstitutionLoading,
  } = useInstitutionDropDownControl({ type: fsType, period, paramKey: 'institutionId' });

  const { limitedList, similars } = useCountryInstitutionSelection({ institutionList: institutions, institution });

  return (
    <SectionWithContent sx={finnoChartStyles.container}>
      <Grid sx={contentStyles}>
        <ChartWizardStep id='type' label='Type' isLoading={isTypeLoading}>
          <ChartDropDownControlComponent zIndex={20} list={types} selectedId={typeId} setter={setTypeId}>
            <ChartDropDownHeader title={fsType?.text || 'Choose a type'} />
          </ChartDropDownControlComponent>
        </ChartWizardStep>

        {fsType && (
          <ChartWizardStep id='pastPeriod' label='Period' isLoading={isPeriodLoading}>
            <ChartDropDownControlComponent zIndex={15} list={periods} selectedId={periodId} setter={setPeriodId}>
              <ChartDropDownHeader title={period?.text || 'Choose a period'} />
            </ChartDropDownControlComponent>
          </ChartWizardStep>
        )}

        {period && (
          <Box id='currentPeriod'>
            <SmallText>Compare List:</SmallText>
            <CountrySelector
              countries={countries}
              colorPickerComponent={
                <Box onClick={(e) => e.stopPropagation()}>
                  <ClosableColorPicker defaultColor={colorA} setColorHandler={setColorA} />
                </Box>
              }
              sx={{ width: '100% !important', zIndex: 10 }}
            />
          </Box>
        )}

        {fsType && period && (
          <ChartWizardStep id='bankSelector' label='Institution' isLoading={isInstitutionLoading}>
            <LegacySwitch items={[{ title: 'Separated' }, { title: 'Mixed' }]} value={isMixed} changeHandler={setIsMixed} />
            <InstitutionDropDownWithState
              institutions={institutions}
              onSelect={institutionSetHandler}
              selectedInstitution={institution}
              sx={{ zIndex: 5 }}
              header={
                <ComparisonHeader
                  subject={institution || DefaultChartData.sample}
                  chartComponent={
                    <ChartItem
                      percentage={(institution?.total || DefaultChartData.sample.total) * 10}
                      sx={chartStyles.headerChart}
                      fillComponent={<Monochrome sx={fillColorStyles(colorB)} />}
                    />
                  }
                  colorPickerComponent={
                    <Box onClick={(e) => e.stopPropagation()}>
                      <ClosableColorPicker defaultColor={colorB} setColorHandler={setColorB} />
                    </Box>
                  }
                />
              }
            />
          </ChartWizardStep>
        )}

        {institution && (
          <Box id='downloadButton'>
            <ExportButton element={exportChartRef.current} type='chart' name={`international.${institution.name}`} />
          </Box>
        )}

        <Box id='chart' variant={institution ? undefined : 'styles.sampleChart'}>
          <InternationalComparisonChart
            ref={exportChartRef}
            institutions={institution ? limitedList : DefaultChartData.international.banks}
            currentInstitution={institution || DefaultChartData.international.banks[0]}
            similarInstitutions={similars || DefaultChartData.international.similars}
            isMixed={isMixed === 1}
          />
        </Box>
      </Grid>
    </SectionWithContent>
  );
};
